import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const CameraIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>);

const inputStyle = { width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:15, padding:'12px 14px', outline:'none' };
const labelStyle = { fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', display:'block', marginBottom:6 };

export default function ProfilePage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef();

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    if (data) {
      setProfile(data);
      setName(data.name || '');
      setPhotoUrl(data.photo_url || '');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem não pode ter mais de 5MB.');
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `avatar-${user.id}.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      setPhotoUrl(publicUrl);
    } catch (err) { alert('Error: ' + err.message); }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    await supabase.from('profiles').upsert({ id: user.id, name, photo_url: photoUrl, email: user.email });
    setSaving(false);
    setSuccess(true);
    setTimeout(() => { setSuccess(false); navigate('/'); }, 1500);
  };

  const initials = name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2) : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', maxWidth:'var(--app-width, 860px)', margin:'0 auto' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes slideDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:none} }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
      `}</style>
      <div className="bg-grid"/>

      {success && (
        <div style={{ position:'fixed', top:20, left:'50%', transform:'translateX(-50%)', background:'#22C55E', color:'#fff', padding:'10px 24px', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14, letterSpacing:1, zIndex:999, animation:'slideDown 0.3s ease' }}>
          GUARDADO ✓
        </div>
      )}

      {/* Header */}
      <div style={{ padding:'52px 20px 32px', background:'linear-gradient(180deg, #FF550015 0%, #111009 100%)', position:'relative', zIndex:1, textAlign:'center' }}>
        <button onClick={() => navigate('/')} style={{ position:'absolute', top:52, left:20, background:'transparent', color:'#7A7268', display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
          <BackIcon/> HOME
        </button>

        {/* Avatar */}
        <div style={{ position:'relative', display:'inline-block', marginBottom:16 }}>
          {photoUrl
            ? <img src={photoUrl} alt="" style={{ width:100, height:100, borderRadius:'50%', objectFit:'cover', border:'3px solid #FF5500' }}/>
            : <div style={{ width:100, height:100, borderRadius:'50%', background:'#FF5500', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, fontWeight:800, color:'#fff', border:'3px solid #FF5500' }}>{initials}</div>
          }
          <button onClick={() => fileRef.current.click()} style={{ position:'absolute', bottom:2, right:2, width:30, height:30, borderRadius:'50%', background:'#1C1916', border:'2px solid #FF5500', display:'flex', alignItems:'center', justifyContent:'center', color:'#FF5500' }}>
            {uploading ? <div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid #FF550033', borderTopColor:'#FF5500', animation:'spin 0.7s linear infinite' }}/> : <CameraIcon/>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display:'none' }}/>
        </div>

        <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:26, fontWeight:900, letterSpacing:1, color:'#F0EBE3' }}>{name || 'My Profile'}</h1>
        <div style={{ fontSize:12, color:'#5A5248', marginTop:4 }}>{user?.email}</div>
      </div>

      {/* Form */}
      <div style={{ padding:'24px 20px', position:'relative', zIndex:1 }}>
        <div style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:16, padding:'24px 20px', marginBottom:16 }}>
          <div style={{ marginBottom:20 }}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Your name"/>
          </div>
          <div style={{ marginBottom:4 }}>
            <label style={labelStyle}>Email</label>
            <input style={{ ...inputStyle, color:'#5A5248', background:'#171410' }} value={user?.email || ''} disabled/>
          </div>
          <div style={{ fontSize:11, color:'#4A4038', marginTop:6 }}>Email cannot be changed</div>
        </div>

        <button onClick={save} disabled={saving} style={{ width:'100%', padding:'15px', background:'#FF5500', color:'#fff', borderRadius:12, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:16, letterSpacing:2, marginBottom:12 }}>
          {saving ? 'SAVING…' : 'SAVE CHANGES'}
        </button>

        {/* Divider */}
        <div style={{ height:1, background:'#1E1C18', margin:'24px 0' }}/>

        {/* Danger zone */}
        <div style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:16, padding:'20px' }}>
          <div style={{ fontFamily:"'Barlow Condensed'", fontSize:12, fontWeight:800, letterSpacing:2, color:'#7A7268', marginBottom:16 }}>ACCOUNT</div>
          <button onClick={signOut} style={{ width:'100%', padding:'13px', background:'#2A1010', color:'#FF6666', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:14, letterSpacing:1 }}>
            SIGN OUT
          </button>
        </div>
      </div>
    </div>
  );
}
